import { useMemo, useState } from 'react';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

export const useTableControls = (
  data,
  {
    initialSortKey = null,
    initialSortDirection = 'asc',
    initialPageSize = DEFAULT_PAGE_SIZE,
  } = {}
) => {
  const [sortKey, setSortKey] = useState(initialSortKey);
  const [sortDirection, setSortDirection] = useState(initialSortDirection);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(1);

  const sortedData = useMemo(() => {
    const source = Array.isArray(data) ? [...data] : [];
    if (!sortKey) return source;

    source.sort((a, b) => {
      const aValue = a?.[sortKey];
      const bValue = b?.[sortKey];

      if (aValue === bValue) return 0;
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const normalizedA = String(aValue).toLowerCase();
      const normalizedB = String(bValue).toLowerCase();
      if (normalizedA < normalizedB) return sortDirection === 'asc' ? -1 : 1;
      if (normalizedA > normalizedB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return source;
  }, [data, sortKey, sortDirection]);

  const totalRows = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);

  const paginatedData = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, safePage, pageSize]);

  const onSort = (key) => {
    if (!key) return;
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
    setPage(1);
  };

  const onPageSizeChange = (nextSize) => {
    setPageSize(nextSize);
    setPage(1);
  };

  const goToPreviousPage = () => setPage((prev) => Math.max(1, prev - 1));
  const goToNextPage = () => setPage((prev) => Math.min(totalPages, prev + 1));

  return {
    page,
    pageSize,
    pageSizes: DEFAULT_PAGE_SIZES,
    sortKey,
    sortDirection,
    totalRows,
    totalPages,
    data: paginatedData,
    onSort,
    onPageSizeChange,
    setPage,
    goToPreviousPage,
    goToNextPage,
  };
};

